import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import Loading from '../components/Loading';
import {
  getExperience,
  getGeoXpConfig,
  getSupabaseSession,
  getUserExperiences,
  getUserProfile,
  signOut,
} from '../utils/supabase';
import Login from '../views/Login';
import { useLocalStorage } from '../hooks/useLocalStorage';
import { AGAMI_GLOBAL_EXPERIENCE_ID, EXPERIENCES_STORAGE_KEY } from '../../config/config';
import useToast from '../hooks/useToast';
import { useNetwork } from '../hooks/useNetwork';
import { getDeviceOsAndBrowser } from '../utils/utils';
import { useLanguage } from './language';

const SessionContext = React.createContext(null);

const sortById = (a, b) => {
  if (a.id < b.id) {
    return -1;
  }
  if (a.id > b.id) {
    return 1;
  }
  return 0;
};

export default function SessionProvider({ children }) {
  const mounted = useRef(false);

  const location = useLocation();
  const { content } = useLanguage();

  const [user, setUser] = useState(null);
  const [offline, setOffline] = useState(null);
  const [loading, setLoading] = useState(true);
  const [experiences, setExperiences] = useState([]);
  const [selectedExperience, setSelectedExperience] = useState(null);
  const [savedExperiences, setSavedExperiences] = useLocalStorage(EXPERIENCES_STORAGE_KEY, []);

  const toast = useToast();
  const networkAvailable = useNetwork();

  const getSession = useCallback(async () => {
    if (!networkAvailable) return;
    const { data: session } = await getSupabaseSession();
    if (!session) {
      setUser(undefined);
      setLoading(false);
      return;
    }

    const { data: profile } = await getUserProfile();
    if (!profile) {
      setUser(undefined);
      Sentry.setUser(null);
      setLoading(false);
      return;
    }

    // we can only set ID here for privacy compliances
    Sentry.setUser({
      id: session.user?.id,
    });

    setUser({
      id: session.user?.id,
      completeName: profile.name ? `${profile?.name || ''} ${profile?.surname || ''}` : undefined,
      email: session.user?.email,
      confirmedAt: session.user?.confirmed_at,
      role: profile.roles?.role_name,
      ...profile,
    });

    const { data: experiencesData } = await getUserExperiences({ userId: session.user?.id });
    if (!experiencesData) {
      setExperiences(savedExperiences.map((e) => ({ ...e, fromStorage: true })));
      return;
    }
    // excludes agami global and user default library from experiences list
    setExperiences(
      experiencesData
        .filter((e) => e.experience_id !== AGAMI_GLOBAL_EXPERIENCE_ID && !e.experiences?.is_default)
        .map((e) => {
          const alreadySaved = savedExperiences.find((s) => s.id === e.experience_id);
          return {
            id: e.experience_id,
            label: e.experiences.name,
            language: e.experiences.main_language,
            welcome_message: e.experiences.welcome_message,
            description: e.experiences.description,
            offlineEnabled: !e.access_code_id,
            nfcEnabled: e.experiences.nfc_trigger,
            qrEnabled: e.experiences.qr_trigger,
            lightOnboarding: e.experiences.light_onboarding,
            creditsEnabled: e.experiences.credits_enabled,
            creditsBlocks: e.experiences.credits_blocks,
            ...(alreadySaved
              ? {
                  fromStorage: true,
                  downloadError: alreadySaved.downloadError,
                  config: alreadySaved.config,
                }
              : {}),
          };
        })
        .sort(sortById)
    );

    setLoading(false);
  }, [savedExperiences]);

  const offlineMode = useCallback(
    (type) => {
      if (type === null) {
        setOffline(null);
      } else {
        setOffline(type && typeof type === 'string' ? type : 'forced');
      }
      setExperiences(savedExperiences.map((e) => ({ ...e, fromStorage: true })));
      setLoading(false);
    },
    [savedExperiences, networkAvailable]
  );

  const logout = useCallback(async () => {
    await signOut();
  }, []);

  const resetSavedExperiences = useCallback(() => {
    setSavedExperiences([]);
  }, []);

  const getExperienceData = React.useCallback(
    async (experience, options = {}) => {
      const silent = options?.silent ?? false;
      const { data, error } = await getExperience({ experienceId: experience.id });
      if (error) {
        console.error(error);
        if (!silent) toast.error(content('get_experience_data_error'));
        return;
      }
      if (!data) return;

      return {
        ...experience,
        label: data.name,
        description: data.description,
        language: data.main_language,
        welcome_message: data.welcome_message,
        nfcEnabled: data.nfc_trigger,
        qrEnabled: data.qr_trigger,
        lightOnboarding: data.light_onboarding,
        creditsEnabled: data.credits_enabled,
        creditsBlocks: data.credits_blocks,
      };
    },
    [content]
  );

  const getExperienceConfig = useCallback(
    async (experience, options = {}) => {
      const silent = options?.silent ?? false;
      const { data, error } = await getGeoXpConfig({ experienceId: experience.id });
      if (error) {
        console.error(error);
        if (!silent) toast.error(content('get_experience_config_error'));
        return;
      }

      if (!data) return;

      /*
       * Patch config for iOS!
       * autoplaySounds must always be false for iPhone web apps
       * to avoid potential playback blocks from iOS
       */
      const { os } = getDeviceOsAndBrowser();
      const config = {
        ...data,
        audio: {
          ...data.audio,
          options: {
            ...data.audio.options,
            autoplaySounds: os === 'iOS' ? false : data.audio.options.autoplaySounds,
          },
        },
      };

      return { ...experience, config };
    },
    [content]
  );

  const selectExperience = useCallback(async (experience) => {
    if (!experience.config) {
      const experienceWithConfig = await getExperienceConfig(experience);
      setSelectedExperience(experienceWithConfig);
      return experienceWithConfig;
    }
    setSelectedExperience(experience);
    return experience;
  }, []);

  const saveExperience = useCallback(
    async (experience, commit = true, options = {}) => {
      const reset = {
        ...experience,
        fromStorage: false,
        downloadError: false,
      };

      const updated = await getExperienceData(reset, options);
      if (!updated) return { ...experience, downloadError: true };

      const toSave = await getExperienceConfig(updated, options);
      if (!toSave?.config) return { ...experience, downloadError: true };

      if (commit) {
        const updateExperiences = (toUpdate) => {
          const filtered = toUpdate.filter((e) => e.id !== toSave.id);
          return [...filtered, toSave].sort(sortById);
        };

        setSavedExperiences(updateExperiences);
        setExperiences(updateExperiences);
      }

      return toSave;
    },
    [getExperienceData, getExperienceConfig, setSavedExperiences]
  );

  const updateSavedExperience = React.useCallback(
    (experience) => {
      const updateExperiences = (toUpdate) => {
        const filtered = toUpdate.filter((e) => e.id !== experience.id);
        return [...filtered, experience].sort(sortById);
      };
      setSavedExperiences(updateExperiences);
      setExperiences(updateExperiences);

      return experience;
    },
    [setSavedExperiences]
  );

  const lightOnboarding = useMemo(() => selectedExperience?.lightOnboarding !== false, [selectedExperience]);

  useEffect(() => {
    if (networkAvailable) getSession();
    else offlineMode('forced');
  }, []);

  useEffect(() => {
    if (!mounted.current) mounted.current = true;
    else {
      if (!offline && !networkAvailable) offlineMode('no-network');
      if (offline !== 'forced' && networkAvailable) setOffline(null);
    }
  }, [networkAvailable, offline]);

  const contextValue = useMemo(
    () => ({
      user,
      experiences,
      loading,
      selectedExperience,
      offline,
      saveExperience,
      updateSavedExperience,
      selectExperience,
      logout,
      resetSavedExperiences,
      offlineMode,
      lightOnboarding,
    }),
    [
      user,
      experiences,
      loading,
      selectedExperience,
      offline,
      saveExperience,
      updateSavedExperience,
      selectExperience,
      logout,
      resetSavedExperiences,
      offlineMode,
      lightOnboarding,
    ]
  );

  if (loading) return <Loading />;

  if (
    !user &&
    offline !== 'forced' &&
    location.pathname !== '/redeem-experience' &&
    location.pathname !== '/redeem-experience-anonymous' &&
    location.pathname !== '/manual-code'
  )
    return <Login success={getSession} offlineMode={offlineMode} />;

  return <SessionContext.Provider value={contextValue}>{children}</SessionContext.Provider>;
}

export const useSession = () => React.useContext(SessionContext);
